/**
 * Curriculum addressing — the one place a curriculum key is built.
 *
 * Every node of the tree (Subject · Unit · Chapter · SubTopic) is addressable by
 * a dotted path, and content is tagged with one. Two forms, differing only in
 * whether the academic year is present:
 *
 *   key         cbse.2026-27.10.mathematics.algebra.quadratic-equations.nature-of-roots
 *   conceptKey  cbse.10.mathematics.algebra.quadratic-equations.nature-of-roots
 *
 * `key` identifies a node IN ONE YEAR and is the upsert target: re-running the
 * seed, re-importing a syllabus, or an admin repeating an authoring step all
 * land on the same row rather than minting a second copy. `conceptKey` survives
 * the roll-over, so a question tagged with it is found by next year's chapter
 * without being re-tagged — which is what makes cloning a year cheap and what
 * makes newly authored content appear under the right chapter automatically.
 *
 * Built here and nowhere else. A second implementation that lowercased
 * differently, or joined on '/', would produce keys that look right and match
 * nothing — and the failure would be silent: content simply never appears.
 */
import { gradeLabel, type Board, type Grade } from './constants';

/** Separator for path segments. A segment may therefore never contain a dot. */
const SEPARATOR = '.';

/** A segment is lowercase alphanumerics and single hyphens. */
const SEGMENT_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Normalize a display name into a path segment.
 *
 * Lossy by design — 'Pair of Linear Equations in Two Variables' and its
 * re-worded successor should collide, because they are the same chapter and the
 * questions written for one belong to the other. Where two genuinely different
 * siblings normalize alike, the unique index on (parent, slug) rejects the
 * second and the author picks a distinct slug.
 */
export function curriculumSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export function isValidCurriculumSlug(slug: string): boolean {
  return slug.length > 0 && slug.length <= 80 && SEGMENT_PATTERN.test(slug);
}

/**
 * A node's position in the tree, in slug form, from the root down.
 *
 * Every level below the one being addressed is simply absent — the same shape
 * addresses a Subject and a SubTopic, so callers never assemble a partial path
 * by hand.
 */
export interface CurriculumPath {
  board: Board;
  grade: Grade;
  /** The AcademicYear slug. Omitted by `conceptKey`, required by `curriculumKey`. */
  yearSlug?: string;
  subject?: string;
  unit?: string;
  chapter?: string;
  subtopic?: string;
}

function tail(path: CurriculumPath): string[] {
  const segments: string[] = [];
  for (const value of [path.subject, path.unit, path.chapter, path.subtopic]) {
    // Stop at the first gap rather than skipping it: a path with a chapter but
    // no unit is a caller bug, and silently producing a shorter key would tag
    // content against a node that does not exist.
    if (!value) break;
    segments.push(value);
  }
  return segments;
}

/** Year-specific node identity. The upsert key. */
export function curriculumKey(path: CurriculumPath & { yearSlug: string }): string {
  return [path.board.toLowerCase(), path.yearSlug, gradeLabel(path.grade), ...tail(path)].join(
    SEPARATOR,
  );
}

/** Year-independent identity. What content is tagged with. */
export function conceptKey(path: CurriculumPath): string {
  return [path.board.toLowerCase(), gradeLabel(path.grade), ...tail(path)].join(SEPARATOR);
}

/**
 * Normalize an academic-year label into its slug. '2026-27', '2026 - 2027' and
 * '2026/27' are the same year written three ways, and a school will write all
 * three across two imports.
 *
 * An alias for `curriculumSlug` today, and deliberately its own name: a year
 * label is the one segment a human types free-hand, so if it ever needs its own
 * normalization the call sites already say which rule they wanted.
 */
export function academicYearSlug(label: string): string {
  return curriculumSlug(label);
}

/**
 * The roster-facing classroom code: `cbse-grade-10_a`, or `cbse-grade-10` where
 * the workspace has no sections.
 *
 * This is what a bulk-import row names, which is the whole constraint on its
 * shape — an admin retypes it from a spreadsheet, so it is lowercase, has no
 * spaces, and is matched case-insensitively on the way in.
 *
 * The BOARD is part of it because the code has to be unambiguous within a
 * workspace, and a workspace can run two: the first-party tenant teaches CBSE
 * and ICSE side by side, and so do plenty of schools. Without it, 'grade-10_a'
 * names two different classes and an import row silently picks whichever the
 * unique index happened to keep.
 */
export function classroomCode(board: Board, grade: Grade, section?: string | null): string {
  const base = `${board.toLowerCase()}-grade-${gradeLabel(grade)}`;
  const suffix = section ? curriculumSlug(section) : '';
  return suffix ? `${base}_${suffix}` : base;
}

/** The display name that pairs with `classroomCode`. */
export function classroomName(board: Board, grade: Grade, section?: string | null): string {
  const base = `${board} Grade ${gradeLabel(grade)}`;
  return section ? `${base} ${section.trim()}` : base;
}

/** Case-insensitive match for a code typed into an import. */
export function normalizeClassroomCode(raw: string): string {
  return raw.trim().toLowerCase();
}
